import {
  Button,
  Chip,
  Drawer,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { PublishingChannelView } from '../../../api/src/telegram/publishing.types';
import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { Box, Stack, Typography } from '../components/mui-system';

const date = (value: number | null, zone: string) =>
  value === null ? '—' : new Date(value).toLocaleString('ru-RU', { timeZone: zone });

function channelState(c: PublishingChannelView) {
  const errors = [...new Set([c.error, c.sourceError].filter(Boolean))];
  const shortage = c.enabled && !c.blocked && !errors.length && c.remaining > c.eligible;
  return {
    errors,
    shortage,
    label: c.blocked
      ? 'Нужна проверка'
      : errors.length
        ? 'Ошибка'
        : shortage
          ? 'Мало анкет'
          : c.enabled
            ? 'По расписанию'
            : 'Пауза',
    color: c.blocked
      ? ('warning' as const)
      : errors.length
        ? ('error' as const)
        : shortage
          ? ('warning' as const)
          : c.enabled
            ? ('success' as const)
            : ('default' as const),
  };
}

function ChannelState({ channel: c }: { channel: PublishingChannelView }) {
  const state = channelState(c);
  return (
    <Stack gap={0.5} alignItems="flex-start" sx={{ overflowWrap: 'anywhere' }}>
      <Chip
        size="small"
        label={state.label}
        color={state.color}
        sx={{
          maxWidth: '100%',
          height: 'auto',
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 },
        }}
      />
      {c.blocked && (
        <Typography variant="body2" color="warning.main">
          Результат отправки неизвестен. Публикации остановлены — проверьте историю.
        </Typography>
      )}
      {state.errors.map((error) => (
        <Typography key={error} variant="body2" color="error.main">
          {error}
        </Typography>
      ))}
      {state.shortage && (
        <Typography variant="body2" color="warning.main">
          До дневной нормы осталось {c.remaining}, доступно {c.eligible}.
        </Typography>
      )}
    </Stack>
  );
}

const available = (c: PublishingChannelView) =>
  c.sourceError || c.refreshedAt === null ? 'Неизвестно' : c.eligible;
const next = (c: PublishingChannelView) =>
  c.blocked ? 'Остановлено' : date(c.nextPublicationAt, c.timeZone);

export function PublishingChannelTable({
  channels,
  onOpen,
}: {
  channels: PublishingChannelView[];
  onOpen: (id: string) => void;
}) {
  return (
    <TableContainer>
      <Table size="small" aria-label="Каналы публикаций" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: { xs: '36%', md: '25%' } }}>Канал / город</TableCell>
            <TableCell sx={{ width: { xs: '40%', md: '29%' } }}>Состояние</TableCell>
            <TableCell sx={{ width: { xs: '24%', md: '12%' }, px: { xs: 0.5, md: 2 } }}>
              Сегодня
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: '22%' }}>
              Следующая публикация
            </TableCell>
            <TableCell
              align="right"
              sx={{ display: { xs: 'none', md: 'table-cell' }, width: '12%' }}
            >
              Доступно анкет
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {channels.map((c) => (
            <TableRow key={c.id} hover sx={{ '& td': { py: 1.5, verticalAlign: 'top' } }}>
              <TableCell sx={{ overflowWrap: 'anywhere' }}>
                <Button
                  variant="text"
                  aria-label={`Открыть канал ${c.title}`}
                  onClick={() => onOpen(c.id)}
                  sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {c.title}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {c.cityName} · {c.timeZone}
                </Typography>
              </TableCell>
              <TableCell>
                <ChannelState channel={c} />
              </TableCell>
              <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                <Tooltip title={`Опубликовано / план · ${c.today} · ${c.timeZone}`}>
                  <Box component="span">
                    {c.sentToday} / {c.dailyTarget}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{next(c)}</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {available(c)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function PublishingChannelDrawer({
  channel: c,
  busy,
  error,
  notice,
  onClose,
  onEnabled,
  onEdit,
  onRefresh,
  onPreview,
  onHistory,
  onSkipped,
}: {
  channel: PublishingChannelView;
  busy: boolean;
  error: string;
  notice: string;
  onClose: () => void;
  onEnabled: (enabled: boolean) => void;
  onEdit: () => void;
  onRefresh: () => void;
  onPreview: () => void;
  onHistory: () => void;
  onSkipped: () => void;
}) {
  const canSchedule = useCanEdit('telegram.schedule');
  const canPublish = useCanEdit('telegram.publish');
  return (
    <Drawer
      anchor="right"
      open
      onClose={() => !busy && onClose()}
      slotProps={{
        paper: {
          sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100%' },
          role: 'dialog',
          'aria-label': `Канал ${c.title}`,
        },
      }}
    >
      <Stack p={{ xs: 2, sm: 3 }} gap={2} sx={{ overflowWrap: 'anywhere' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3">{c.title}</Typography>
            <Typography color="text.secondary">
              {c.cityName} · {c.timeZone}
            </Typography>
          </Box>
          <IconButton aria-label="Закрыть канал" disabled={busy} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {error && (
          <Typography role="alert" color="error.main">
            {error}
          </Typography>
        )}
        {notice && (
          <Typography role="status" color="success.main">
            {notice}
          </Typography>
        )}
        <ChannelState channel={c} />
        {canSchedule && (c.enabled || canPublish) && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>Расписание публикаций</Typography>
            <Switch
              checked={c.enabled}
              disabled={busy}
              slotProps={{ input: { 'aria-label': `Расписание ${c.title}` } }}
              onChange={(_, enabled) => onEnabled(enabled)}
            />
          </Stack>
        )}
        <Stack
          component="dl"
          gap={1.5}
          sx={{ m: 0, '& dt': { color: 'text.secondary' }, '& dd': { m: 0 } }}
        >
          <Box>
            <Typography component="dt">Сегодня · {c.today}</Typography>
            <Typography component="dd">
              {c.sentToday} / {c.dailyTarget}
            </Typography>
          </Box>
          <Box>
            <Typography component="dt">Следующая публикация</Typography>
            <Typography component="dd">{next(c)}</Typography>
          </Box>
          <Box>
            <Typography component="dt">Время публикаций</Typography>
            <Typography component="dd">
              {c.startTime}–{c.endTime} · {c.timeZone}
            </Typography>
          </Box>
          <Box>
            <Typography component="dt">Повтор</Typography>
            <Typography component="dd">Через {c.repeatDays} сут.</Typography>
          </Box>
          <Box>
            <Typography component="dt">Доступно анкет</Typography>
            <Typography component="dd">{available(c)}</Typography>
          </Box>
          <Box>
            <Typography component="dt">Каталог</Typography>
            <Typography component="dd">
              Всего: {c.catalogueCount}, пропущено: {c.skippedCount}
            </Typography>
          </Box>
          <Box>
            <Typography component="dt">Каталог обновлён</Typography>
            <Typography component="dd">{date(c.refreshedAt, c.timeZone)}</Typography>
          </Box>
          <Box>
            <Typography component="dt">ID канала</Typography>
            <Typography component="dd">{c.chatId}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <EditorOnly
            permissions={
              c.enabled
                ? ['telegram.edit', 'telegram.schedule', 'telegram.publish']
                : ['telegram.edit', 'telegram.schedule']
            }
          >
            <Button disabled={busy} onClick={onEdit}>
              Настройки
            </Button>
          </EditorOnly>
          <EditorOnly permissions={['telegram.run', 'telegram.publish']}>
            <Button disabled={busy} onClick={onRefresh}>
              Обновить анкеты
            </Button>
          </EditorOnly>
          <Button disabled={busy} onClick={onPreview}>
            Предпросмотр
          </Button>
          <Button disabled={busy} onClick={onHistory}>
            История
          </Button>
          {c.skippedCount > 0 && (
            <Button disabled={busy} onClick={onSkipped}>
              Причины пропуска
            </Button>
          )}
        </Stack>
      </Stack>
    </Drawer>
  );
}
